import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { APP_MAPPING_BY_SLUG } from '../../config';
import { Product } from '../../types';
import { useProductDrawerQuery } from '../../hooks/queries/useProductDrawerQuery';
import { hasWorkspacesAccess } from '../../features/dashboard/components/useWorkspaces';
import LoadingBar from './LoadingBar';

interface ProductAccessRouteProps {
  children: React.ReactNode;
  requiredProductCode?: string;  // For direct product code (e.g., Kaiya)
  useHubToolSlug?: boolean;      // For hub tool mapping (e.g., programs)
  redirectTo?: string;
}

const ProductAccessRoute: React.FC<ProductAccessRouteProps> = ({ 
  children, 
  requiredProductCode,
  useHubToolSlug = false,
  redirectTo = '/' 
}) => {
  const { hubtoolslug } = useParams();
  const { user, isLoading } = useApp();
  const canUseWorkspaces = hasWorkspacesAccess(user);
  const {
    data: drawerProducts = [],
    isLoading: isDrawerLoading,
    isError: isDrawerError,
  } = useProductDrawerQuery(
    user?.id,
    user?.activeWorkspaceId as string | number | undefined,
    canUseWorkspaces
  );
  const fallbackProducts = user?.dashboardProducts ?? user?.products ?? [];
  const products = canUseWorkspaces && !isDrawerError
    ? drawerProducts
    : fallbackProducts;

  // Show loading while user data is being loaded
  if (isLoading || (user && isDrawerLoading)) {
    return <LoadingBar />;
  }

  // If no user (shouldn't happen with ProtectedRoute wrapper), redirect
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  let productCodeToCheck = requiredProductCode;

  // If using hub tool slug mode, derive product code from URL and APP_MAPPING
  if (useHubToolSlug) {
    // If no hubtoolslug parameter, allow access (shouldn't happen with proper routing)
    if (!hubtoolslug) {
      return <>{children}</>;
    }

    // Get the app mapping for this hub tool slug
    const appMapping = APP_MAPPING_BY_SLUG[hubtoolslug as keyof typeof APP_MAPPING_BY_SLUG];
    
    // If no mapping found for this slug, redirect (invalid hub tool)
    if (!appMapping) {
      return <Navigate to={redirectTo} replace />;
    }

    productCodeToCheck = appMapping.code;
  }

  // Must have a product code to check by this point
  if (!productCodeToCheck) {
    console.error('ProductAccessRoute: No product code specified');
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user has the required product
  const hasRequiredProduct = products.some((product: Product) =>
    product.code === productCodeToCheck
  );

  // If user doesn't have required product, redirect
  if (!hasRequiredProduct) {
    return <Navigate to={redirectTo} replace />;
  }

  // User has required product, render children
  return <>{children}</>;
};

export default ProductAccessRoute;